import { useEffect, useState } from 'react';
import usePagedOptions from './usePagedOptions';
import * as bookingFormsApi from '../api/bookingForms';
import { isPickable } from '../lib/louFromAgreement';

const PAGE = 20;
const SEARCH_DEBOUNCE = 300;

/**
 * The agent's own agreements of one type ('OTP' | 'OTR'), for "Fill from an OTP /
 * OTR" on the CID Forms letters (lib/louFromAgreement). Searchable by reference or
 * address, paged, newest first — the phone's bottom sheet and the desktop's modal
 * both read it.
 *
 * Cancelled agreements are dropped when SHOWN, not when loaded: a page filtered
 * short is how usePagedOptions decides the feed has ended. A failed load is said
 * as such (`error`) rather than shown as "you have no agreements".
 */
export default function useAgreementOptions(type, open) {
  const [q, setQ] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const lookup = usePagedOptions(async (term, page) => {
    try {
      const r = await bookingFormsApi.listBookingForms({ type, page, limit: PAGE, ...(term ? { search: term } : {}) });
      const rows = (r?.data ?? r) || [];
      if (page === 1) setError(null);
      return rows.map((row) => ({ value: row.id, row }));
    } catch (e) {
      if (page === 1) setError(e.message || 'Could not load your agreements');
      throw e;
    } finally {
      if (page === 1) setLoading(false);
    }
  }, PAGE);

  // Opening primes page 1 at once; typing re-searches a beat after the last key.
  useEffect(() => {
    if (!open || !type) return undefined;
    setLoading(true);
    const t = setTimeout(() => lookup.search(q.trim()), q ? SEARCH_DEBOUNCE : 0);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, type, q]);

  // A fresh search every time the picker opens.
  useEffect(() => { if (!open) setQ(''); }, [open]);

  return {
    q,
    setQ,
    loading,
    error,
    rows: lookup.options.map((o) => o.row).filter(isPickable),
    hasMore: lookup.hasMore,
    loadingMore: lookup.loadingMore,
    loadMore: lookup.loadMore,
  };
}
